import ReactMarkdown from 'react-markdown'
import type { Components } from 'react-markdown'
import remarkGfm from 'remark-gfm'

const components: Components = {
  h1: ({ children }) => (
    <h1 className="mt-6 first:mt-0 mb-3 text-lg font-semibold text-cream font-display">
      {children}
    </h1>
  ),
  h2: ({ children }) => (
    <h2 className="mt-5 first:mt-0 mb-2 text-base font-semibold text-cream font-display">
      {children}
    </h2>
  ),
  h3: ({ children }) => (
    <h3 className="mt-4 first:mt-0 mb-2 text-sm font-semibold text-cream uppercase tracking-wide">
      {children}
    </h3>
  ),
  p: ({ children }) => <p className="my-3 first:mt-0 last:mb-0 leading-relaxed">{children}</p>,
  a: ({ href, children }) => (
    <a
      href={href}
      target="_blank"
      rel="noreferrer noopener"
      className="text-sky underline underline-offset-2 decoration-sky/40 hover:decoration-sky transition-colors duration-300"
    >
      {children}
    </a>
  ),
  strong: ({ children }) => <strong className="font-semibold text-cream">{children}</strong>,
  em: ({ children }) => <em className="italic text-cream/90">{children}</em>,
  ul: ({ children }) => (
    <ul className="my-3 pl-5 space-y-1.5 list-disc marker:text-ember/50">
      {children}
    </ul>
  ),
  ol: ({ children }) => (
    <ol className="my-3 pl-5 space-y-1.5 list-decimal marker:text-ember/50 marker:font-mono">
      {children}
    </ol>
  ),
  li: ({ children }) => <li className="leading-relaxed">{children}</li>,
  blockquote: ({ children }) => (
    <blockquote className="my-4 border-l-[3px] border-ember/40 pl-4 py-2 bg-ember/5 rounded-r-xl text-mute italic">
      {children}
    </blockquote>
  ),
  hr: () => <hr className="my-6 border-haze/40" />,
  code: ({ inline, className, children, ...props }: any) => {
    if (inline) {
      return (
        <code
          className="px-1.5 py-0.5 bg-ember/8 text-ember font-mono text-[0.88em] rounded-md"
          {...props}
        >
          {children}
        </code>
      )
    }
    return (
      <code className={`${className || ''} font-mono text-[0.9em] text-cream`} {...props}>
        {children}
      </code>
    )
  },
  pre: ({ children }) => (
    <pre className="my-4 bg-ink-raised border border-haze/40 px-5 py-4 overflow-x-auto font-mono text-[0.9em] leading-relaxed rounded-xl shadow-sm">
      {children}
    </pre>
  ),
  table: ({ children }) => (
    <div className="my-4 overflow-x-auto border border-haze/40 rounded-xl shadow-sm">
      <table className="w-full text-sm">{children}</table>
    </div>
  ),
  thead: ({ children }) => <thead className="bg-ink-raised/80 border-b border-haze/40">{children}</thead>,
  th: ({ children }) => (
    <th className="text-left px-4 py-3 text-sm font-semibold text-cream">
      {children}
    </th>
  ),
  td: ({ children }) => <td className="px-4 py-3 border-t border-haze/30 text-sm">{children}</td>,
  tr: ({ children }) => <tr className="even:bg-ink/20">{children}</tr>,
}

export function Markdown({ text }: { text: string }) {
  return (
    <div className="text-cream leading-relaxed">
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
        {text}
      </ReactMarkdown>
    </div>
  )
}
